import isEqual from 'lodash.isequal';
import * as React from 'react';

import { useNewAddResourceMapping } from 'src/containers/ProjectDiagram/hooks/entityDrawer/useAddResourceMapping';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import { MappingSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/projectResourceSuggestionsData';
import { IgnoredSuggestion } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getOrganizedSuggestionRecords';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

/**
 * returns a callback that fetches and then opens the New Mapping dialog (ProjectResourceSuggestionsDialog),
 * as well as the loading state for the fetch
 */
export const useAutoOpenMappingSuggestions = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();

  const classItems = useReduxSelector(
    projectClassItemsSelector(projectId),
    isEqual
  );

  const handleAddMapping = useNewAddResourceMapping();

  const { fetchMappingSuggestions, isLoading } = useFetchMappingSuggestions();

  const autoOpenMappingSuggestions = React.useCallback(
    async (resourceIds: string[]) => {
      let ignoredSuggestions: IgnoredSuggestion[] | null = null;
      let mappingSuggestions: MappingSuggestion[] | null = null;
      if (!classItems.length) {
        const ret = await fetchMappingSuggestions({
          selectedClasses: [],
          selectedResources: resourceIds,
        });
        ({ ignoredSuggestions, mappingSuggestions } = ret);
      }

      handleAddMapping({
        initialIgnoredSuggestions: ignoredSuggestions || undefined,
        initialMappingSuggestions: mappingSuggestions || undefined,
        initialResourceIds: resourceIds,
      });

      dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
    },
    [classItems, dispatch, fetchMappingSuggestions, handleAddMapping]
  );

  return {
    autoOpenMappingSuggestions,
    isLoading,
  };
};
